import Button from 'components/Button';
import { useLayoutContext } from 'contexts/LayoutContext';
import { Row } from 'components/Layout';
import FeeList from '../FeeList';
import { ListLpFeesCategory } from 'further-types/lp/lp-fee';
import { numberToCurrencyString, pluralize } from 'further-ui/utils';
import useFeeManager from 'hooks/lp/data/fee-manager/useFeeManager';
import { ApprovalSummaryData } from '..';
import Page from 'components/new/Page';
import TabsPanel from 'further-ui/components/TabsPanel';
import Statistic from 'components/new/Statistic';
import { useUrlHash } from 'hooks/ui/useUrlHash';
import { match } from 'ts-pattern';

const DEFAULT_TAB = ListLpFeesCategory.DueUnapprovedFees;

const FeeManager: React.FC<{
  setApprovalSummaryData: (data: ApprovalSummaryData) => void;
  onGoToApproval: () => void;
  selectedFeeGroupId: string;
}> = ({ setApprovalSummaryData, onGoToApproval, selectedFeeGroupId }) => {
  const { hash } = useUrlHash();
  const { selectedVehicleId, vehicle } = useLayoutContext();

  const currentTab = hash ?? DEFAULT_TAB;

  const {
    dueUnapprovedFees,
    futureUnapprovedFees,
    approvedFees,
    dueFeesPage,
    futureFeesPage,
    approvedFeesPage,
    setDueFeesPage,
    setFutureFeesPage,
    setApprovedFeesPage,
    exportFeesMutation,
  } = useFeeManager();

  const handleExport = async () => {
    if (!selectedVehicleId) return;

    exportFeesMutation.mutate({
      vehicleId: selectedVehicleId,
    });
  };

  const subheading = match(currentTab)
    .with(ListLpFeesCategory.ApprovedFees, () => {
      const feesCount = approvedFees?.totalCount ?? 0;
      return `You have ${feesCount} ${pluralize(
        'fee',
        feesCount,
      )} approved to date`;
    })
    .with(ListLpFeesCategory.FutureUnapprovedFees, () => {
      const feesCount = futureUnapprovedFees?.totalCount ?? 0;
      return `You have ${feesCount} future unapproved ${pluralize(
        'fee',
        feesCount,
      )}.`;
    })
    .otherwise(() => {
      const feesCount = dueUnapprovedFees?.totalCount ?? 0;
      return `You have ${feesCount} unapproved ${pluralize('fee', feesCount)}.`;
    });

  return (
    <Page
      tabTitle="Management fees"
      heading="Management fees"
      subheading={subheading}
      withHeadingDivider
      actions={[
        {
          variant: 'outlined',
          label: 'Export',
          onClick: handleExport,
          disabled: exportFeesMutation.isPending,
        },
      ]}
      actionBar={
        <Row fullWidth justify="flex-end">
          <Button
            disabled={!selectedFeeGroupId}
            onClick={onGoToApproval}
            variant="contained"
            size="large"
          >
            Go to approve
          </Button>
        </Row>
      }
    >
      <TabsPanel
        keepAlive
        useTabsUrls
        withDivider
        spacing="none"
        tabs={[
          {
            key: ListLpFeesCategory.DueUnapprovedFees,
            urlParam: ListLpFeesCategory.DueUnapprovedFees,
            content: (
              <>
                {dueUnapprovedFees && (
                  <FeeList
                    selectedFeeGroupId={selectedFeeGroupId}
                    currencyLcy={vehicle?.baseCurrency}
                    fees={dueUnapprovedFees}
                    currentPage={dueFeesPage}
                    onPageChange={setDueFeesPage}
                    feeType={ListLpFeesCategory.DueUnapprovedFees}
                    setApprovalSummaryData={setApprovalSummaryData}
                  />
                )}
              </>
            ),
            label: (
              <Statistic
                muted={currentTab !== ListLpFeesCategory.DueUnapprovedFees}
                paddingY="sm"
                paddingX="xs"
                spacing="sm"
                label="Unapproved fees"
                value={numberToCurrencyString(
                  dueUnapprovedFees?.feesTotalValue,
                  { currency: vehicle?.baseCurrency },
                )}
              />
            ),
          },
          {
            key: ListLpFeesCategory.FutureUnapprovedFees,
            urlParam: ListLpFeesCategory.FutureUnapprovedFees,
            content: (
              <>
                {futureUnapprovedFees && (
                  <FeeList
                    selectedFeeGroupId={selectedFeeGroupId}
                    currencyLcy={vehicle?.baseCurrency}
                    fees={futureUnapprovedFees}
                    currentPage={futureFeesPage}
                    onPageChange={setFutureFeesPage}
                    feeType={ListLpFeesCategory.FutureUnapprovedFees}
                    setApprovalSummaryData={setApprovalSummaryData}
                  />
                )}
              </>
            ),
            label: (
              <Statistic
                muted={currentTab !== ListLpFeesCategory.FutureUnapprovedFees}
                spacing="sm"
                paddingY="sm"
                paddingX="xs"
                label="Unapproved future fees"
                value={numberToCurrencyString(
                  futureUnapprovedFees?.feesTotalValue,
                  { currency: vehicle?.baseCurrency },
                )}
              />
            ),
          },
          {
            key: ListLpFeesCategory.ApprovedFees,
            urlParam: ListLpFeesCategory.ApprovedFees,
            content: (
              <>
                {approvedFees && (
                  <FeeList
                    currencyLcy={vehicle?.baseCurrency}
                    fees={approvedFees}
                    currentPage={approvedFeesPage}
                    onPageChange={setApprovedFeesPage}
                    feeType={ListLpFeesCategory.ApprovedFees}
                  />
                )}
              </>
            ),
            label: (
              <Statistic
                muted={currentTab !== ListLpFeesCategory.ApprovedFees}
                spacing="sm"
                paddingY="sm"
                paddingX="xs"
                label="Fees approved to date"
                value={numberToCurrencyString(approvedFees?.feesTotalValue, {
                  currency: vehicle?.baseCurrency,
                })}
              />
            ),
          },
        ]}
      />
    </Page>
  );
};

export default FeeManager;
